import {Inject, injectable} from 'inversify-props';

import {ILogger} from '@/utils/ILogger';
import {LogdownLogger} from '@/utils/LogdownLogger';

import Jwt from '@/model/Jwt';

import {ISettingsService} from '@/services/settings/ISettings.service';
import {Setting} from '@/services/settings/Settings.types';
import {IApiService} from '@/services/IApi.service';
import {IAuthService} from '@/services/IAuth.service';

@injectable()
export class AuthStore {
  public TokenRefreshDelayBeforeExpiryS = 60;

  public TokenRefreshMinPeriodS = 5;

  private logger: ILogger = new LogdownLogger('AuthStore');

  private token: Jwt | null = null;

  @Inject(Symbol.for('ISettingsService'))
  private settingsService!: ISettingsService;

  @Inject(Symbol.for('IApiService'))
  private apiService!: IApiService;

  @Inject(Symbol.for('IAuthService'))
  private authService!: IAuthService;

  private tokenRefreshTimeoutHandler!: any;

  public async login(username: string, password: string): Promise<void> {
    const token = await this.authService.login(username, password);
    this.setToken(token);
  }

  public async refresh(): Promise<void> {
    const token = await this.authService.refresh();
    this.setToken(token);
  }

  public loadToken(): void {
    const tokenString = this.settingsService.get(Setting.Token);

    this.logger.trace('token', tokenString);

    if (!tokenString) {
      return;
    }

    const token = new Jwt(tokenString);
    if (token.expiration < new Date()) {
      this.logger.info('Token expired', token.expiration.toLocaleString());
      return;
    }

    this.setToken(token);
  }

  public setToken(token: Jwt): void {
    this.settingsService.set(Setting.Token, token.string);
    this.apiService.setToken(token);
    this.token = token;

    const refreshDelay = Math.max(
      token.expiration.getTime() - new Date().getTime() - this.TokenRefreshDelayBeforeExpiryS * 1000,
      this.TokenRefreshMinPeriodS * 1000,
    );

    this.logger.info(`Token will expire at ${token.expiration}, refreshing at ${new Date(new Date().getTime() + refreshDelay)} (in ${refreshDelay / 1000}s)`);

    this.tokenRefreshTimeoutHandler = setTimeout(async () => {
      await this.refresh();
    }, refreshDelay);
  }

  get Token(): Jwt | null {
    return this.token;
  }

  get LoggedUsername(): string {
    if (!this.token) {
      return '';
    }

    return this.token.id;
  }

  public logout() {
    clearTimeout(this.tokenRefreshTimeoutHandler);
    this.token = null;
    this.apiService.removeToken();
    this.settingsService.clear(Setting.Token);
  }
}
